import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import chatApi from '@services/chat';

const ChatContext = createContext();

const getWelcomeMessages = (step) => {
	switch (step) {
		case 1:
			return [
				"Hey! Welcome to your Dashboard. Let's get your store set up on Unify by NearSt and sell more 👋",
				"As a first step - I'll help you connect your Google Business Profile and POS system.",
				'I can talk you through it, or you can also dive in directly in the section on the right'
			];
		case 2:
			return [
				"Great progress! You've set up your business details 🎉",
				"Now let's connect your Google Business Profile so customers can find your products online.",
				"I'm here if you need any help with this step"
			];
		case 3:
			return [
				'Almost there! Just one more step to go 🚀',
				"Let's connect your POS system to sync your inventory automatically.",
				'Which POS system do you use? I can help you get connected'
			];
		default:
			return [
				'Hey! Welcome to your Dashboard 👋',
				"I'm here to help you through the onboarding process.",
				'Feel free to ask me anything!'
			];
	}
};

const getStorageKey = (userId) => {
	if (!userId) return null;
	return `unify_chat_session_${userId}`;
};

const generateSessionId = () => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const isOnboardingComplete = (progress) => progress?.businessDetailsCompleted && progress?.gbpCompleted && progress?.posConnectionCompleted;

const animateWelcomeMessages = (texts, setMessages, setIsLoading) => {
	setIsLoading(true);
	texts.forEach((text, index) => {
		setTimeout(
			() => {
				setMessages((prev) => [
					...prev,
					{
						id: Date.now() + index,
						role: 'assistant',
						content: text,
						timestamp: new Date().toISOString(),
						isWelcome: true
					}
				]);
				if (index === texts.length - 1) {
					setIsLoading(false);
				}
			},
			(index + 1) * 800
		);
	});
};

export const ChatProvider = ({
	children,
	mode = 'general', // 'onboarding' | 'general'
	currentShop,
	onboardingProgress = null,
	connectionState = null,
	userId = null
}) => {
	const [messages, setMessages] = useState([]);
	const [isOpen, setIsOpen] = useState(true);
	const [isMobileOpen, setIsMobileOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [sessionId, setSessionId] = useState(null);
	const [failedAttempts, setFailedAttempts] = useState(0);
	const [shownSteps, setShownSteps] = useState(new Set());

	// Refs for tracking welcome message state (defined before load effect so they can be restored)
	const welcomeStartedRef = useRef(false);
	const shownStepsRef = useRef(new Set());

	const markStepAsShown = useCallback((step) => {
		shownStepsRef.current.add(step);
		setShownSteps(new Set(shownStepsRef.current));
	}, []);

	// Load chat history from localStorage when userId is available
	useEffect(() => {
		const storageKey = getStorageKey(userId);
		if (!storageKey) {
			setSessionId(generateSessionId());
			return;
		}

		const savedSession = localStorage.getItem(storageKey);
		if (savedSession) {
			try {
				const {
					messages: savedMessages,
					sessionId: savedId,
					failedAttempts: savedFailed,
					shownSteps: savedShownSteps
				} = JSON.parse(savedSession);
				setMessages(savedMessages || []);
				setSessionId(savedId);
				setFailedAttempts(savedFailed || 0);
				// Restore shown steps tracking
				if (savedShownSteps && Array.isArray(savedShownSteps)) {
					const restoredSteps = new Set(savedShownSteps);
					setShownSteps(restoredSteps);
					shownStepsRef.current = restoredSteps;
				}
				// Set welcomeStartedRef if messages exist
				if (savedMessages && savedMessages.length > 0) {
					welcomeStartedRef.current = true;
				}
			} catch (err) {
				console.error('Failed to load chat history:', err);
				setSessionId(generateSessionId());
			}
		} else {
			setSessionId(generateSessionId());
		}
	}, [userId]);

	// Save to localStorage whenever messages or shownSteps change
	useEffect(() => {
		const storageKey = getStorageKey(userId);
		if (storageKey && sessionId && messages.length > 0) {
			localStorage.setItem(
				storageKey,
				JSON.stringify({
					messages,
					sessionId,
					failedAttempts,
					shownSteps: Array.from(shownSteps),
					timestamp: Date.now()
				})
			);
		}
	}, [messages, sessionId, failedAttempts, shownSteps, userId]);

	const clearHistory = useCallback(() => {
		setMessages([]);
		setFailedAttempts(0);
		setShownSteps(new Set());
		shownStepsRef.current.clear();
		welcomeStartedRef.current = false;
		const storageKey = getStorageKey(userId);
		if (storageKey) {
			localStorage.removeItem(storageKey);
		}
		setSessionId(generateSessionId());
	}, [userId]);

	// Clear history when onboarding completes (onboarding mode only)
	useEffect(() => {
		if (mode === 'onboarding' && isOnboardingComplete(onboardingProgress)) {
			clearHistory();
		}
	}, [mode, onboardingProgress, clearHistory]);

	const getCurrentStep = (progress) => {
		if (!progress) return 1;
		if (!progress.businessDetailsCompleted) return 1;
		if (!progress.gbpCompleted) return 2;
		if (!progress.posConnectionCompleted) return 3;
		return 'complete';
	};

	// Animate welcome messages on initial load (onboarding mode only)
	useEffect(() => {
		if (mode !== 'onboarding') return;
		if (welcomeStartedRef.current || messages.length > 0 || !sessionId) return;

		welcomeStartedRef.current = true;

		const step = getCurrentStep(onboardingProgress);
		const welcomeTexts = getWelcomeMessages(step);

		if (typeof step === 'number') {
			markStepAsShown(step);
		}

		animateWelcomeMessages(welcomeTexts, setMessages, setIsLoading);
	}, [mode, sessionId, onboardingProgress]);

	// Detect step changes and add transition messages (onboarding mode only)
	// Only triggers for steps that haven't shown their welcome messages yet
	useEffect(() => {
		if (mode !== 'onboarding') return;
		if (!sessionId || messages.length === 0) return;

		const currentStep = getCurrentStep(onboardingProgress);

		// Skip if not a valid step number or already shown
		if (typeof currentStep !== 'number' || shownStepsRef.current.has(currentStep)) {
			return;
		}

		markStepAsShown(currentStep);

		const transitionMessages = getWelcomeMessages(currentStep);
		animateWelcomeMessages(transitionMessages, setMessages, setIsLoading);
	}, [mode, onboardingProgress, sessionId, messages.length]);

	const sendMessage = async (content) => {
		// Add user message
		const userMessage = {
			id: Date.now(),
			role: 'user',
			content,
			timestamp: new Date().toISOString()
		};
		setMessages((prev) => [...prev, userMessage]);
		setIsLoading(true);

		try {
			// Build context from onboarding state
			const context = {
				currentStep: getCurrentStep(onboardingProgress),
				shopData: currentShop
					? {
							id: currentShop.id,
							name: currentShop.name,
							plan: currentShop.plan,
							hasGBPConnection: !!currentShop.gmbConnection?.status,
							hasPOSConnection: !!currentShop.inventorySources?.find((s) => s.kind === 'stock'),
							inventorySources: currentShop.inventorySources,
							// Include selected POS information
							selectedPOS: connectionState?.stock?.selectedPOS,
							selectedPOSProvider: connectionState?.stock?.provider?.label
						}
					: null
			};

			// Call backend API
			const response = await chatApi.sendMessage({
				sessionId,
				message: content,
				context,
				conversationHistory: messages.slice(-10) // Last 10 messages for context
			});

			// Add assistant response
			const assistantMessage = {
				id: Date.now() + 1,
				role: 'assistant',
				content: response.message,
				timestamp: new Date().toISOString()
			};
			setMessages((prev) => [...prev, assistantMessage]);

			// Reset failed attempts on success
			setFailedAttempts(0);
		} catch (error) {
			console.error('Chat error:', error);

			// Increment failed attempts
			const newFailedAttempts = failedAttempts + 1;
			setFailedAttempts(newFailedAttempts);

			// Add error message
			let errorContent = 'Sorry, I encountered an error. Please try again.';

			// Show support escalation after 3 failed attempts
			if (newFailedAttempts >= 3) {
				errorContent =
					"I'm having trouble responding right now. If you need immediate help, please contact our support team at support@near.st. They typically respond within 1 business day.";
			}

			const errorMessage = {
				id: Date.now() + 1,
				role: 'assistant',
				content: errorContent,
				timestamp: new Date().toISOString(),
				isError: true
			};
			setMessages((prev) => [...prev, errorMessage]);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<ChatContext.Provider
			value={{
				messages,
				isOpen,
				setIsOpen,
				isMobileOpen,
				setIsMobileOpen,
				isLoading,
				sendMessage,
				clearHistory,
				sessionId,
				failedAttempts,
				currentStep: getCurrentStep(onboardingProgress),
				currentShop
			}}
		>
			{children}
		</ChatContext.Provider>
	);
};

export const useChat = () => {
	const context = useContext(ChatContext);
	if (!context) {
		throw new Error('useChat must be used within a ChatProvider');
	}
	return context;
};
